import * as React from "react";
import { cn } from "@/lib/utils";

interface CardHeaderWithBadgesProps {
  /** 제목 */
  title: string;
  /** 부제목 또는 설명 */
  subtitle?: string;
  /** 버전 텍스트 (제목 옆에 표시) */
  version?: string;
  /** 아이콘 (이모지 또는 React 노드) */
  icon?: React.ReactNode;
  /** 우측 상단에 표시할 배지들 */
  badges?: React.ReactNode[];
  /** 추가 클래스명 */
  className?: string;
  /** 제목 클래스명 */
  titleClassName?: string;
  /** 삭제/비활성 상태 */
  muted?: boolean;
}

/**
 * CardHeaderWithBadges - 카드 헤더 + 배지 레이아웃 컴포넌트
 *
 * 제목과 부제목을 왼쪽에, 배지들을 오른쪽에 배치하는 레이아웃
 *
 * @example
 * <CardHeaderWithBadges
 *   title="서비스 이름"
 *   subtitle="AI / 챗봇"
 *   version="1.2.0"
 *   badges={[
 *     <Badge variant="success">활성</Badge>,
 *     <Badge variant="info">정상</Badge>,
 *   ]}
 * />
 */
const CardHeaderWithBadges = React.forwardRef<
  HTMLDivElement,
  CardHeaderWithBadgesProps
>(
  (
    {
      title,
      subtitle,
      version,
      icon,
      badges,
      className,
      titleClassName,
      muted = false,
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn("flex items-start justify-between", className)}
      >
        {/* 왼쪽: 아이콘 + 제목 + 부제목 */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center">
            {icon && (
              <span className="text-xl mr-2 flex-shrink-0">{icon}</span>
            )}
            <h3
              className={cn(
                "text-lg font-semibold truncate",
                muted && "text-gray-500",
                titleClassName
              )}
            >
              {title}
            </h3>
            {version && (
              <span className="text-xs text-gray-400 font-normal ml-2 flex-shrink-0">
                v{version}
              </span>
            )}
          </div>
          {subtitle && (
            <p
              className={cn(
                "text-sm mt-1 truncate",
                muted ? "text-gray-400" : "text-gray-500"
              )}
            >
              {subtitle}
            </p>
          )}
        </div>

        {/* 오른쪽: 배지들 */}
        {badges && badges.length > 0 && (
          <div className="flex flex-col items-end gap-1.5 ml-3 flex-shrink-0">
            {badges.map((badge, index) => (
              <React.Fragment key={index}>{badge}</React.Fragment>
            ))}
          </div>
        )}
      </div>
    );
  }
);

CardHeaderWithBadges.displayName = "CardHeaderWithBadges";

export { CardHeaderWithBadges };
